// frontend/src/screens/admin/sps/ImportDemographicsScreen.jsx
import { useState, useRef } from 'react';
import { Container, Row, Col, Card, Button, Alert, Table, Badge } from 'react-bootstrap';
import { useBatchImport }   from '../../../hooks/useBatchImport';
import ImportProgress       from '../../../components/sps/ImportProgress';
import BasketballLoader     from '../../../components/sps/BasketballLoader';

const COLUMN_MAP = [
  { file: 'Employee Name',         stored: 'employeeName',        note: 'Required' },
  { file: 'PPS Number',            stored: 'ppsNumber',           note: 'Key'      },
  { file: 'Employee Number',       stored: 'employeeNumber',      note: ''         },
  { file: 'Grade',                 stored: 'grade',               note: ''         },
  { file: 'Pay Group',             stored: 'payGroup',            note: ''         },
  { file: 'Pay Group Description', stored: 'payGroupDesc',        note: ''         },
  { file: 'Date Of Birth',         stored: 'dateOfBirth',         note: 'ABS'      },
  { file: 'Start Date',            stored: 'employmentStartDate', note: ''         },
  { file: 'End Date',              stored: 'employmentEndDate',   note: 'Leaver'   },
  { file: 'Home Address Line 1',   stored: 'addressLine1',        note: 'ABS'      },
  { file: 'Home Address Line 2',   stored: 'addressLine2',        note: ''         },
  { file: 'Home Address Line 3',   stored: 'addressLine3',        note: ''         },
  { file: 'Home Address Line 4',   stored: 'addressLine4',        note: ''         },
  { file: 'Email / Email Address', stored: 'email',               note: 'Optional' },
];

const PRESERVED = ['civilStatus','pensionsAdjustmentOrder','normalRetirementDate','membershipCategory','notes'];

function DropZone({ onFile, disabled }) {
  const [over, setOver] = useState(false);
  const ref = useRef(null);
  const style = {
    border: `2px dashed ${over ? '#1e3a5f' : '#ced4da'}`, borderRadius: 8,
    padding: '3.5rem 2rem', textAlign: 'center',
    background: over ? '#e8f0fa' : '#fafafa',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all .2s', opacity: disabled ? 0.6 : 1,
  };
  return (
    <div style={style}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
      onClick={() => !disabled && ref.current?.click()}
    >
      <div style={{ fontSize: '2.8rem', marginBottom: '.5rem' }}>📋</div>
      <div className="fw-semibold fs-6">Drag &amp; drop the HR demographics Excel file here</div>
      <div className="text-muted small mt-1">
        accepts .xlsx &nbsp;·&nbsp; sheet must be named <strong>Data</strong>
      </div>
      <input ref={ref} type="file" accept=".xlsx,.xlsm,.xls" style={{ display: 'none' }}
        disabled={disabled} onChange={(e) => onFile(e.target.files[0])} />
    </div>
  );
}

export default function ImportDemographicsScreen() {
  const { startImport, state, reset } = useBatchImport({
    endpoint:  '/api/staff-directory/import-demographics-batch',
    sheetName: 'Data',
    batchSize: 200,  // 200 rows × ~150 bytes = ~30kb per POST
  });

  const [selectedFile, setSelectedFile] = useState(null);

  const isRunning = state.status === 'reading' || state.status === 'importing';
  const isDone    = state.status === 'done';

  const handleFile = (file) => { setSelectedFile(file); reset(); };
  const handleImport = () => { if (selectedFile) startImport(selectedFile); };

  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">Import Staff Demographics</h4>
          <small className="text-muted">
            File is read in the browser — progress bar shows exact rows confirmed by server
          </small>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg={7}>
          <Card className="mb-3">
            <Card.Header className="fw-semibold bg-light">Upload HR Demographics File</Card.Header>
            <Card.Body>

              {isRunning
                ? <BasketballLoader isRunning={isRunning} />
                : <DropZone onFile={handleFile} disabled={false} />
              }

              {selectedFile && !isRunning && !isDone && (
                <Alert variant="info" className="mt-3 mb-0 d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <div>
                    <strong>{selectedFile.name}</strong>
                    <span className="ms-2 text-muted small">({(selectedFile.size / 1024).toFixed(0)} KB)</span>
                  </div>
                  <Button variant="primary" onClick={handleImport}>⬆ Import Now</Button>
                </Alert>
              )}

              <ImportProgress state={state} />

              {isDone && (
                <div className="mt-2">
                  <Button variant="outline-secondary" size="sm"
                    onClick={() => { setSelectedFile(null); reset(); }}>
                    Import another file
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="fw-semibold bg-light">Import Rules</Card.Header>
            <Card.Body className="small text-muted">
              <ul className="mb-0 ps-3" style={{ lineHeight: 1.8 }}>
                <li>Records upserted on PPS Number — existing members updated, new ones created.</li>
                <li>Fields never overwritten: {PRESERVED.join(', ')}.</li>
                <li><strong>Email</strong> is updated when the HR file provides a value — a blank column leaves the existing email unchanged.</li>
                <li>Members with an End Date are automatically marked as inactive.</li>
                <li>Progress bar updates after each batch of 500 rows confirmed by server.</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={5}>
          <Card className="mb-3">
            <Card.Header className="fw-semibold bg-light">
              Expected Column Names
            </Card.Header>
            <Card.Body className="p-0">
              <Table size="sm" className="mb-0" style={{ fontSize: '.78rem' }}>
                <thead className="table-dark">
                  <tr><th>Column in Excel</th><th>Stored as</th><th style={{ width: 70 }}>Notes</th></tr>
                </thead>
                <tbody>
                  {COLUMN_MAP.map((col) => (
                    <tr key={col.file}>
                      <td className="font-monospace" style={{ fontSize: '.73rem' }}>{col.file}</td>
                      <td className="font-monospace text-primary" style={{ fontSize: '.73rem' }}>{col.stored}</td>
                      <td>
                        {col.note === 'Required' && <Badge bg="danger"           style={{ fontSize: '.6rem' }}>Required</Badge>}
                        {col.note === 'Key'      && <Badge bg="danger"           style={{ fontSize: '.6rem' }}>Key</Badge>}
                        {col.note === 'ABS'      && <Badge bg="info" text="dark" style={{ fontSize: '.6rem' }}>ABS</Badge>}
                        {col.note === 'Leaver'   && <Badge bg="warning" text="dark" style={{ fontSize: '.6rem' }}>Leaver</Badge>}
                        {col.note === 'Optional' && <Badge bg="secondary"            style={{ fontSize: '.6rem' }}>Optional</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="fw-semibold bg-light">Fields Preserved on Re-Import</Card.Header>
            <Card.Body className="small">
              <div className="d-flex flex-wrap gap-2">
                {PRESERVED.map((f) => (
                  <Badge key={f} bg="secondary" style={{ fontSize: '.72rem', fontWeight: 400 }}>{f}</Badge>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
